import { useSelector } from "react-redux";
import {
  Grid,
  Stack,
  Divider,
  Pill,
  Typography,
  LodgeAIBadge,
  useBreakpoints,
  useFinanceableTheme,
} from "@financeable-com-au/financeable-ui";
import type { ApplicationFormSlice, UserDetailsState } from "@/store/slices/types/applicationFormSlice";
// @ts-expect-error: ignoring as it is existing js file
import { userDetailsSelector } from "@/store/slices/applicationFormSlice";
import { CustomRatesFees } from "@/components/LenderProductCard/CustomRatesFees";
import { CustomRepayments } from "@/components/LenderProductCard/CustomRepayments";
import { DefaultRatesFees } from "@/components/LenderProductCard/DefaultRatesFees";
import { CriteriaProgress } from "@/components/LenderProductCard/CriteriaProgress";
import { FieldProgress } from "@/components/LenderProductCard/FieldProgress";
import { useLenderCardContext } from "@/providers/LenderCardContextProvider";
import { DefaultRepayments } from "@/components/LenderProductCard/DefaultRepayments";
import { ApiLodgePill } from "@/components/ApiLodgeBadge";
import { useFeatureFlags } from "@/utils/featureFlags/useFeatureFlags";
import { isLodgeAISupported } from "@/utils/lenderUtils";
import colors from "@/style/colors";

interface LenderCardInfoProps {
  isCustomQuote: boolean;
  isProductQuoteExists?: boolean;
}

const LenderCardInfo = ({ isCustomQuote, isProductQuoteExists }: LenderCardInfoProps) => {
  const { isLodgeAIEnabled } = useFeatureFlags();
  const theme = useFinanceableTheme();
  const {
    product,
    lenderCard: { documents },
  } = useLenderCardContext();
  const { smUp } = useBreakpoints();
  // If product is undefined, return null.
  if (!product) return null;

  const { logo, lender, isApiLodge } = product;
  // Use isQuoteOpen to determine if this is part of a quote flow.
  const { isQuoteOpen } = useSelector<ApplicationFormSlice, UserDetailsState>(userDetailsSelector);
  const isApplication = !isQuoteOpen;
  const showLodgeAIBadge = isLodgeAISupported(product) && isLodgeAIEnabled();

  return (
    <Grid container columnSpacing={{ xs: 0, sm: 5 }} rowSpacing={{ xs: 4, sm: 0 }}>
      <Grid xs={12} sm={2}>
        {isProductQuoteExists && (
          <Typography
            level="title-sm"
            component="span"
            style={{
              backgroundColor: theme.palette.primary[200],
              padding: "0 10px",
              color: colors.white,
              margin: "-20px 0 0 -25px",
              width: "fit-content",
              borderRadius: "6px 0 10px 0",
            }}
          >
            Quote
          </Typography>
        )}
        <Stack flexDirection={{ xs: "column", sm: "row" }} justifyContent="space-between" height="100%">
          {logo && <img width="80%" height="auto" style={{ objectFit: "contain" }} src={logo} alt={lender} />}
          <Divider orientation={smUp ? "vertical" : "horizontal"} />
        </Stack>
      </Grid>
      <Grid xs={12} sm={6}>
        <Stack flexDirection="column" rowGap={4} flexGrow={1}>
          {isCustomQuote ? <CustomRepayments /> : <DefaultRepayments />}
          <Stack>
            <CriteriaProgress />
            {isApplication && <FieldProgress />}
            {product.disclaimer && (
              <Typography level="body-xs" style={{ marginTop: "10px" }}>
                <strong>Disclaimer:</strong> {product.disclaimer}
              </Typography>
            )}
          </Stack>
        </Stack>
      </Grid>
      <Grid xs={12} sm={4}>
        <Stack
          flexDirection="row"
          justifyContent="flex-end"
          alignItems="center"
          columnGap={2}
          mb={2}
          flexWrap="wrap"
          rowGap={1}
        >
          {showLodgeAIBadge && <LodgeAIBadge size="xs" />}
          {!!isApiLodge && <ApiLodgePill />}
          {documents?.map(({ name, colour }) => (
            <Pill accent={colour} key={name}>
              {name}
            </Pill>
          ))}
        </Stack>
        {isCustomQuote ? <CustomRatesFees /> : <DefaultRatesFees />}
      </Grid>
    </Grid>
  );
};

export { LenderCardInfo };
